import { SIDE_MENU_NAMESPACE } from "features/app-layout/side-menu-choice/side-menu-document";
import { emptySplitApi } from "services/empty-split-api";

import { OPT_IN_NAMESPACE } from "../../domain/settings-document";

/**
 * The per-user cloud settings store (ENG-2047). `GET` returns every namespace this user
 * has in the active practice; `PUT` replaces one namespace's document.
 *
 * Inert until `state/use-settings-sync.hook.ts` mounts (PR 3): nothing here is called and
 * no component subscribes.
 */

/** One namespace as the server returns it. The document is opaque to the transport. */
export interface SchedulingSettingsRecord {
  document: unknown;
  updated_at: string;
}

/** `{}` is a successful empty read — a user who has never saved anything. */
export type SchedulingSettingsResponse = Record<
  string,
  SchedulingSettingsRecord
>;

/**
 * The cache key, not the URL. The endpoint answers for the bearer token's own user in
 * their active practice, and both are in here so that a user switch or a practice switch
 * lands in a different cache entry instead of serving the previous tenant's document.
 * `practiceId` is also what a write's `practice_id` is checked against (409 otherwise).
 */
export interface SchedulingSettingsArgs {
  userId: number;
  practiceId: number;
}

export interface PutSchedulingSettingsArgs {
  namespace: string;
  /** Validated server-side against the active practice; never a key the client picks. */
  practiceId: number;
  document: unknown;
  /**
   * For the `visibilitychange` flush, where the tab may be discarded before the request
   * settles. It reaches `fetch` because RTK's `FetchArgs` extends `RequestInit`
   * (`utils/services.util.ts:86-90`), and unlike `sendBeacon` it keeps the Authorization
   * header — which is the whole reason this is a `fetch` and not a beacon.
   */
  keepalive?: boolean;
}

export interface PutSchedulingSettingsResponse {
  namespace: string;
  document: unknown;
  updated_at: string;
}

export const schedulingSettingsApi = emptySplitApi.injectEndpoints({
  endpoints: (build) => ({
    /**
     * No `providesTags`: these rows are this user's alone, so nothing else in the app can
     * invalidate them, and the sync hook keeps the entry current with `updateQueryData`
     * from the PUT response rather than refetching.
     */
    getSchedulingSettings: build.query<
      SchedulingSettingsResponse,
      SchedulingSettingsArgs
    >({
      query: () => "/auth/scheduling-settings/"
    }),
    /**
     * ENG-1981: the person's "use the new scheduler" document alone, for the app shell's
     * routing. The same URL as `getSchedulingSettings` but its own cache entry, because it
     * needs a timeout that one must not have: the scheduler's hydration deliberately accepts
     * a document that arrives after its own 3 s cap (`use-settings-hydration.hook.ts`), and a
     * timeout would turn that late answer into an error. A read that fails or hangs here
     * just means the classic scheduler, which is the safe default.
     */
    getSchedulingOptIn: build.query<unknown, SchedulingSettingsArgs>({
      query: () => ({ url: "/auth/scheduling-settings/", timeout: 5_000 }),
      // One try: RTK's timeout aborts the request, which also ends the app's timeout-retry
      // loop (`utils/services.util.ts`) — measured, see the hook's "never answers" test.
      transformResponse: (response: SchedulingSettingsResponse) =>
        response?.[OPT_IN_NAMESPACE]?.document ?? null
    }),
    /**
     * ENG-2535: the person's side-menu choice (`side_menu` namespace), read exactly like the
     * scheduler opt-in above: its own cache entry, a 5 s timeout, and a failed read means
     * the default. The app shell asks this on every load, so it must never hang the page.
     */
    getSideMenuChoice: build.query<unknown, SchedulingSettingsArgs>({
      query: () => ({ url: "/auth/scheduling-settings/", timeout: 5_000 }),
      transformResponse: (response: SchedulingSettingsResponse) =>
        response?.[SIDE_MENU_NAMESPACE]?.document ?? null
    }),
    /**
     * No `invalidatesTags`: invalidating the read would refetch the document we just
     * sent, and the response already carries it.
     */
    putSchedulingSettings: build.mutation<
      PutSchedulingSettingsResponse,
      PutSchedulingSettingsArgs
    >({
      query: ({ namespace, practiceId, document, keepalive }) => ({
        url: `/auth/scheduling-settings/${namespace}/`,
        method: "PUT",
        body: { practice_id: practiceId, document },
        ...(keepalive ? { keepalive: true } : {})
      })
    })
  })
});

export const {
  useGetSideMenuChoiceQuery,
  useGetSchedulingOptInQuery,
  useGetSchedulingSettingsQuery,
  usePutSchedulingSettingsMutation
} = schedulingSettingsApi;
